/**
 * @fileoverview
 * Has Permission Directive - Conditionally shows/hides elements based on permissions
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Directive, Input, TemplateRef, ViewContainerRef, inject, OnInit, OnDestroy, effect } from '@angular/core';
import { AuthService } from '../services';

@Directive({
  selector: '[appHasPermission]',
  standalone: true,
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  private templateRef = inject(TemplateRef<any>);
  private viewContainer = inject(ViewContainerRef);
  private authService = inject(AuthService);

  private requiredPermissions: string[] = [];
  private mode: 'all' | 'any' = 'any';
  private hasView = false;

  @Input() set appHasPermission(permissions: string | string[]) {
    this.requiredPermissions = Array.isArray(permissions) ? permissions : [permissions];
    this.updateView();
  }

  @Input() set appHasPermissionMode(mode: 'all' | 'any') {
    this.mode = mode;
    this.updateView();
  }

  // Effect to reactively update view when permissions change
  private permissionEffect = effect(() => {
    // Access permissions signal to trigger reactivity
    this.authService.hasPermission('');
    this.updateView();
  });

  ngOnInit(): void {
    this.updateView();
  }

  ngOnDestroy(): void {
    // Effect cleanup is handled automatically
  }

  private updateView(): void {
    if (!this.requiredPermissions.length) {
      this.showView();
      return;
    }

    const hasAccess = this.mode === 'all'
      ? this.authService.hasAllPermissions(this.requiredPermissions)
      : this.authService.hasAnyPermission(this.requiredPermissions);

    if (hasAccess && !this.hasView) {
      this.showView();
    } else if (!hasAccess && this.hasView) {
      this.hideView();
    }
  }

  private showView(): void {
    if (!this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    }
  }

  private hideView(): void {
    if (this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}

